import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateBoolean, validateString } from '/imports/validators';
import { EnphaseSystem, EnphaseAccessToken as _EnphaseAccessToken } from '/server/integrations/enphase';

export interface EnphaseMeter extends Omit<Doc, 'name'>, EnphaseSystem {
  _id: string;
  meterId: string;
  tokenId: string;
  productionSource?: 'meter' | 'micro'; // telemetry endpoint feeding this meter; falsy → probe 'meter' first (see getEnphaseProduction)
  productionSourceProbedAt?: Date;       // last meter probe; gates the periodic micro→meter re-probe
}
export type Model = EnphaseMeter;

export const EnphaseMeters = new Mongo.Collection<EnphaseMeter>('enphaseMeters');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: EnphaseMeters,
  editableFields: ['active'],
  listMethod: DeclareMethod<ListMethodArg<EnphaseMeter>, ListMethodResponse<EnphaseMeter, Refs>>('listEnphaseMeters'),
  getMethod: DeclareMethod<string, GetMethodResponse<EnphaseMeter, Refs>>('getEnphaseMeter'),
  createMethod: DeclareMethod<Partial<EnphaseMeter>, string>('createEnphaseMeter'),
  updateMethod: DeclareMethod<Partial<EnphaseMeter>, string>('updateEnphaseMeter'),
  deleteMethod: DeclareMethod<string>('deleteEnphaseMeter'),
  pathPrefix: '/enphase-meters',
  asString: (document) => document.name || document.apiKey || document._id,
  modelName: 'Enphase',
  pluralName: 'Enphases',
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
        { identifier: new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (document: Partial<EnphaseMeter>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, document, 'active');
    validateString(errors, document, 'meterId', { required: true });
    return errors;
  },
};

// export const addEnphaseMeter = DeclareMethod<Omit<EgaugeConfig, '_id'>, string>('ds.addEnphaseMeter');
/** Pull recent interval data for a single Enphase meter. */
export const pullEnphaseMeterData = DeclareMethod<{ _id: string, days?: number }>('ds.pullEnphaseMeterData');

Meteor.isServer && Meteor.startup(async () => {
  await EnphaseMeters.createIndexAsync({ meterId: 1 });
});

export interface EnphaseAccessToken extends _EnphaseAccessToken {
  _id: string;
  orgId: string;
  username: string;
}

export const EnphaseAccessTokens = new Mongo.Collection<EnphaseAccessToken>('enphaseAccessTokens');

/** Exchange an Enphase OAuth code for an access token and store it for the org/user. */
export const handleEnphaseAccessTokenCode = DeclareMethod<{ code: string, orgId: string, username: string }>('handleEnphaseAccessTokenCode');
/** List stored Enphase access tokens for an org. */
export const getEnphaseAccessTokensForOrg = DeclareMethod<string, EnphaseAccessToken[]>('getEnphaseAccessTokensForOrg');
/** Pull Enphase systems for a token, creating meter records; returns counts. */
export const pullEnphaseSystemsForToken = DeclareMethod<string, { numSystems: number, numCreated: number }>('pullEnphaseSystemsForToken');

Meteor.isServer && Meteor.startup(async () => {
  await EnphaseAccessTokens.createIndexAsync({ orgId: 1 });
});